import { type ReactNode, useEffect } from 'react'

import { BillingAutoTopUpCard } from '@/components/billing/billing-auto-top-up-card.molecule'
import { BillingBalanceCard } from '@/components/billing/billing-balance-card.molecule'
import { BillingPaymentMethodWarning } from '@/components/billing/billing-payment-method-warning.molecule'
import { BillingRedeemCodeCard } from '@/components/billing/billing-redeem-code-card.molecule'
import { BillingSubscriptionModal } from '@/components/billing/billing-subscription-modal.molecule'
import { BillingTopUpModal } from '@/components/billing/billing-top-up-modal.molecule'
import { EmbeddedCheckoutDrawer } from '@/components/billing/embedded-checkout.organism'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { useEmbeddedCheckout } from '@/hooks/billing/use-embedded-checkout.hook'

import { handleCheckoutReturnParams } from '@/utils/checkout-return.util'

import { BalanceTokenUsageSection } from './balance-token-usage-section.view'
import { useBalanceSettings } from './use-balance-settings.hook'

const UPGRADE_BANNER_MESSAGE = 'Upgrade to a paid plan to add credits and unlock premium features.'

const AutoTopUpCardSkeleton: React.FC = () => (
  <div className="billing-auto-top-up-card-skeleton h-full rounded-xl border bg-card p-6">
    <div className="mb-4 flex items-center justify-between">
      <Skeleton className="h-5 w-24" />
      <Skeleton className="h-5 w-10 rounded-full" />
    </div>
    <Skeleton className="mb-4 h-4 w-40" />
    <Skeleton className="h-10 w-full" />
  </div>
)

interface BalanceCardsGridProps {
  settings: ReturnType<typeof useBalanceSettings>
}

const BalanceCardsGrid: React.FC<BalanceCardsGridProps> = ({ settings }) => (
  <div className="billing-cards-grid grid grid-cols-1 gap-4 md:grid-cols-3">
    <BillingBalanceCard balance={settings.balance} onTopUp={settings.handleTopUpClick} />
    <BillingRedeemCodeCard
      onRedeem={settings.handleRedeemCode}
      isLoading={settings.isRedeemLoading}
    />
    {settings.isAutoTopUpLoading ? (
      <AutoTopUpCardSkeleton />
    ) : (
      <BillingAutoTopUpCard
        settings={settings.autoTopUpSettings}
        isPendingEnable={settings.isAutoTopUpPendingEnable}
        onToggle={settings.handleAutoTopUpToggle}
        onSave={settings.handleAutoTopUpSave}
      />
    )}
  </div>
)

interface BalanceModalsProps {
  settings: ReturnType<typeof useBalanceSettings>
}

const BalanceModals: React.FC<BalanceModalsProps> = ({ settings }) => (
  <>
    <BillingTopUpModal
      isOpen={settings.isTopUpModalOpen}
      onOpenChange={settings.setIsTopUpModalOpen}
      onTopUp={settings.handleTopUp}
      isLoading={settings.isTopUpLoading}
    />
    <BillingSubscriptionModal
      isOpen={settings.isSubscriptionModalOpen}
      onOpenChange={settings.setIsSubscriptionModalOpen}
      planList={settings.planList}
      onSelectPlan={settings.handleSelectPlan}
      isLoading={settings.isSubscribing}
      bannerMessage={UPGRADE_BANNER_MESSAGE}
    />
  </>
)

export const BalanceSettingsView: React.FC = (): ReactNode => {
  useEffect(() => {
    handleCheckoutReturnParams()
  }, [])
  const { checkoutState, openCheckout, closeCheckout, handleCheckoutComplete } =
    useEmbeddedCheckout()
  const settings = useBalanceSettings(
    (clientSecret) => openCheckout(clientSecret),
    (clientSecret) => openCheckout(clientSecret)
  )

  return (
    <div className="balance-settings-view space-y-8">
      {!settings.hasPaymentMethod && (
        <BillingPaymentMethodWarning
          onAddPaymentMethod={settings.handleAddPaymentMethod}
          isLoading={settings.isAddingPaymentMethod}
        />
      )}
      <BalanceCardsGrid settings={settings} />
      <Separator />
      <BalanceTokenUsageSection />
      <BalanceModals settings={settings} />
      <EmbeddedCheckoutDrawer
        isOpen={checkoutState.isOpen}
        onOpenChange={(isOpen) => {
          if (!isOpen) closeCheckout()
        }}
        clientSecret={checkoutState.clientSecret}
        onComplete={handleCheckoutComplete}
      />
    </div>
  )
}

BalanceSettingsView.displayName = 'BalanceSettingsView'
